import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { useFormik } from "formik";
import { Provider, ContactFormSubmission } from "../../types";
import { getProviderByUsername, submitContactForm, getMockProvider } from "../../utils";
import {
  StyledBioPageContainer,
  StyledHeroSection,
  StyledProfileImage,
  StyledProviderName,
  StyledHeadline,
  StyledContactInfo,
  StyledSection,
  StyledSectionTitle,
  StyledBioText,
  StyledServicesGrid,
  StyledServiceCard,
  StyledServiceTitle,
  StyledServiceDescription,
  StyledServiceFeatures,
  StyledServicePricing,
  StyledContactForm,
  StyledFormGroup,
  StyledLabel,
  StyledInput,
  StyledTextarea,
  StyledErrorMessage,
  StyledSubmitButton,
  StyledSuccessMessage,
  StyledErrorAlert,
  StyledLoadingContainer,
  StyledNotFoundContainer,
  StyledSocialLinks,
  StyledRecaptchaNotice,
} from "./ProviderBioPage.styled";

// reCAPTCHA type declarations
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

interface FormValues {
  name: string;
  email: string;
  phone: string;
  message: string;
  website?: string; // Honeypot field
}

export default function ProviderBioPage() {
  const { username } = useParams<{ username: string }>();
  const [provider, setProvider] = useState<Provider | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [emailCopied, setEmailCopied] = useState<boolean>(false);
  const [bioExpanded, setBioExpanded] = useState<boolean>(false);
  const [selectedService, setSelectedService] = useState<any>(null);

  // Fetch provider data on mount
  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchProvider = async () => {
      if (!username) {
        setError("No provider username specified");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // For development: Use mock data
        // Remove this and uncomment the API call when backend is ready
        const mockProvider = getMockProvider(username);
        setProvider(mockProvider);

        // Uncomment when backend API is ready:
        // const data = await getProviderByUsername(username);
        // if (!data) {
        //   setError("Provider not found");
        // } else {
        //   setProvider(data);
        // }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching provider:", err);
        setError("Failed to load provider information. Please try again later.");
        setLoading(false);
      }
    };

    fetchProvider();
  }, [username]);

  // Contact form with Formik
  const formik = useFormik<FormValues>({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
    validate: (values) => {
      const errors: Partial<FormValues> = {};

      if (!values.name.trim()) {
        errors.name = "Name is required";
      }

      if (!values.email.trim()) {
        errors.email = "Email is required";
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
        errors.email = "Invalid email address";
      }

      if (!values.message.trim()) {
        errors.message = "Message is required";
      } else if (values.message.trim().length < 10) {
        errors.message = "Message must be at least 10 characters";
      }

      return errors;
    },
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!provider) return;

      setFormError(null);
      setFormSubmitted(false);

      // Honeypot check - if phone or website filled, it's a bot
      if (values.phone || values.website) {
        console.log('Bot detected via honeypot');
        // Silently fail - don't let bot know it was caught
        setSubmitting(false);
        return;
      }

      try {
        // Get reCAPTCHA token
        const recaptchaToken = await getReCaptchaToken();

        const submission: ContactFormSubmission = {
          providerUsername: provider.username,
          name: values.name,
          email: values.email,
          message: values.message,
          submittedAt: new Date().toISOString(),
          recaptchaToken,
        };

        const result = await submitContactForm(submission);

        if (result.success) {
          setFormSubmitted(true);
          resetForm();
        } else {
          setFormError(result.message);
        }
      } catch (err) {
        setFormError("Failed to send message. Please try again later.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Loading state
  if (loading) {
    return (
      <StyledBioPageContainer>
        <StyledLoadingContainer>
          <h2>Loading provider information...</h2>
        </StyledLoadingContainer>
      </StyledBioPageContainer>
    );
  }

  // Error state
  if (error || !provider) {
    return (
      <StyledBioPageContainer>
        <StyledNotFoundContainer>
          <h1>Provider Not Found</h1>
          <p>{error || "The provider you're looking for doesn't exist."}</p>
          <Link to="/">Return to Home</Link>
        </StyledNotFoundContainer>
      </StyledBioPageContainer>
    );
  }

  // Get initials for placeholder image
  const getInitials = (name: string): string => {
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Copy email to clipboard
  const handleCopyEmail = async () => {
    if (!provider?.email) return;

    try {
      await navigator.clipboard.writeText(provider.email);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  // Get reCAPTCHA token
  const getReCaptchaToken = async (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (typeof window.grecaptcha === 'undefined') {
        console.warn('reCAPTCHA not loaded, proceeding without token');
        resolve('');
        return;
      }

      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY || '', { action: 'submit' })
          .then((token: string) => {
            resolve(token);
          })
          .catch((error: any) => {
            console.error('reCAPTCHA error:', error);
            resolve(''); // Proceed without token rather than blocking user
          });
      });
    });
  };

  return (
    <HelmetProvider>
      <Helmet>
        {/* SEO Meta Tags */}
        <title>{provider.seo.metaTitle}</title>
        <meta name="description" content={provider.seo.metaDescription} />
        <meta name="keywords" content={provider.seo.keywords.join(", ")} />

        {/* Geographic Targeting */}
        {provider.location && (
          <>
            <meta name="geo.region" content={`US-${provider.location.state}`} />
            <meta name="geo.placename" content={provider.location.city} />
            {provider.location.latitude && provider.location.longitude && (
              <meta name="geo.position" content={`${provider.location.latitude};${provider.location.longitude}`} />
            )}
            <meta name="ICBM" content={`${provider.location.latitude}, ${provider.location.longitude}`} />
          </>
        )}

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={provider.seo.metaTitle} />
        <meta property="og:description" content={provider.seo.metaDescription} />
        {provider.seo.ogImage && <meta property="og:image" content={provider.seo.ogImage} />}
        {provider.seo.canonicalUrl && <meta property="og:url" content={provider.seo.canonicalUrl} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={provider.seo.metaTitle} />
        <meta name="twitter:description" content={provider.seo.metaDescription} />
        {provider.seo.ogImage && <meta name="twitter:image" content={provider.seo.ogImage} />}

        {/* Canonical URL */}
        {provider.seo.canonicalUrl && <link rel="canonical" href={provider.seo.canonicalUrl} />}

        {/* reCAPTCHA v3 - badge hidden per Google's terms, which allow
            hiding it as long as the required attribution text (rendered
            near the contact form below) is shown instead */}
        <style>{`.grecaptcha-badge { visibility: hidden; }`}</style>
        <script src={`https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}`}></script>

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: provider.name,
            description: provider.bio,
            email: provider.email,
            telephone: provider.phone,
            url: provider.seo.canonicalUrl || window.location.href,
            image: provider.profileImageUrl || provider.seo.ogImage,
            address: provider.location ? {
              "@type": "PostalAddress",
              addressLocality: provider.location.city,
              addressRegion: provider.location.state,
              postalCode: provider.location.zipCode,
              addressCountry: "US",
            } : {
              "@type": "PostalAddress",
            },
            areaServed: provider.location?.serviceAreas?.map(area => ({
              "@type": "City",
              name: area,
            })) || [],
            geo: provider.location?.latitude && provider.location?.longitude ? {
              "@type": "GeoCoordinates",
              latitude: provider.location.latitude,
              longitude: provider.location.longitude,
            } : undefined,
            priceRange: "$$",
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "5",
              reviewCount: "1",
            },
          })}
        </script>
      </Helmet>

      <StyledBioPageContainer>
        {/* Hero Section */}
        <StyledHeroSection>
          <StyledProfileImage imageUrl={provider.profileImageUrl}>
            {!provider.profileImageUrl && getInitials(provider.name)}
          </StyledProfileImage>
          <StyledProviderName>{provider.name}</StyledProviderName>
          <StyledHeadline>{provider.headline}</StyledHeadline>
          <StyledContactInfo>
            <span
              onClick={handleCopyEmail}
              style={{
                cursor: 'pointer',
                userSelect: 'none',
                color: '#608cdf',
                textDecoration: 'underline',
                display: 'inline-block'
              }}
              title="Click to copy email address"
            >
              📧 Email
              {emailCopied ? (
                <span style={{ color: '#27ae60', fontSize: '0.9em', marginLeft: '5px' }}>✓ Copied!</span>
              ) : (
                <span style={{ color: '#7f8c8d', fontSize: '0.85em', marginLeft: '5px' }}>(click to copy)</span>
              )}
            </span>
          </StyledContactInfo>

          {/* Social Links */}
          {provider.socialLinks && (
            <StyledSocialLinks>
              {provider.socialLinks.twitter && (
                <a
                  href={provider.socialLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Twitter"
                >
                  𝕏
                </a>
              )}
              {provider.socialLinks.instagram && (
                <a
                  href={provider.socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  IG
                </a>
              )}
              {provider.socialLinks.website && (
                <a
                  href={provider.socialLinks.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Website"
                >
                  🌐
                </a>
              )}
            </StyledSocialLinks>
          )}
        </StyledHeroSection>

        {/* About Me Section */}
        <StyledSection>
          <StyledSectionTitle>About Me</StyledSectionTitle>
          <div style={{ position: 'relative' }}>
            <StyledBioText
              style={{
                maxHeight: bioExpanded ? 'none' : '19.2em',
                overflow: 'hidden',
                position: 'relative',
                lineHeight: '1.6em'
              }}
            >
              {provider.bio}
            </StyledBioText>
            {!bioExpanded && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '4em',
                  background: 'linear-gradient(to bottom, transparent, white)',
                  pointerEvents: 'none'
                }}
              />
            )}
          </div>
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button
              onClick={() => setBioExpanded(!bioExpanded)}
              style={{
                background: 'none',
                border: 'none',
                color: '#608cdf',
                cursor: 'pointer',
                fontSize: '1rem',
                textDecoration: 'underline',
                padding: '0.5rem 1rem'
              }}
            >
              {bioExpanded ? 'Read Less ▲' : 'Read More ▼'}
            </button>
          </div>
        </StyledSection>

        {/* Services Section */}
        {provider.services && provider.services.length > 0 && (
          <StyledSection>
            <StyledSectionTitle>Services Offered</StyledSectionTitle>
            <StyledServicesGrid>
              {provider.services.map((service) => (
                <StyledServiceCard key={service.id}>
                  <StyledServiceTitle>{service.title}</StyledServiceTitle>
                  <StyledServiceDescription>
                    {service.description}
                  </StyledServiceDescription>
                  {service.features && service.features.length > 0 && (
                    <StyledServiceFeatures>
                      {service.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </StyledServiceFeatures>
                  )}
                  <button
                    onClick={() => setSelectedService(service)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#608cdf',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      textDecoration: 'underline',
                      padding: '0.5rem 0',
                      marginBottom: '0.5rem',
                      display: 'block'
                    }}
                  >
                    More about this service →
                  </button>
                  {service.pricing && (
                    <StyledServicePricing>{service.pricing}</StyledServicePricing>
                  )}
                </StyledServiceCard>
              ))}
            </StyledServicesGrid>
          </StyledSection>
        )}

        {/* Contact Form Section */}
        {provider.acceptsContactForm && (
          <StyledSection>
            <StyledSectionTitle>Get In Touch</StyledSectionTitle>
            <p style={{ textAlign: "center", marginBottom: "20px", color: "#666" }}>
              Interested in my services? Send me a message and I'll get back to you as soon as possible.
            </p>

            {formSubmitted ? (
              <StyledSuccessMessage>
                Thank you for your message! I'll get back to you soon.
              </StyledSuccessMessage>
            ) : (
              <StyledContactForm onSubmit={formik.handleSubmit}>
                {formError && <StyledErrorAlert>{formError}</StyledErrorAlert>}

                <StyledFormGroup>
                  <StyledLabel htmlFor="name">
                    Name <span style={{ color: "#e74c3c" }}>*</span>
                  </StyledLabel>
                  <StyledInput
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Your full name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={
                      formik.touched.name && formik.errors.name ? "error" : ""
                    }
                  />
                  {formik.touched.name && formik.errors.name && (
                    <StyledErrorMessage>{formik.errors.name}</StyledErrorMessage>
                  )}
                </StyledFormGroup>

                <StyledFormGroup>
                  <StyledLabel htmlFor="email">
                    Email <span style={{ color: "#e74c3c" }}>*</span>
                  </StyledLabel>
                  <StyledInput
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={
                      formik.touched.email && formik.errors.email ? "error" : ""
                    }
                  />
                  {formik.touched.email && formik.errors.email && (
                    <StyledErrorMessage>{formik.errors.email}</StyledErrorMessage>
                  )}
                </StyledFormGroup>

                {/* Honeypot fields - hidden from humans, visible to bots */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                  <input
                    type="text"
                    name="phone"
                    id="phone"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    aria-hidden="true"
                  />
                  <input
                    type="text"
                    name="website"
                    id="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formik.values.website}
                    onChange={formik.handleChange}
                    aria-hidden="true"
                  />
                </div>

                <StyledFormGroup>
                  <StyledLabel htmlFor="message">
                    Message <span style={{ color: "#e74c3c" }}>*</span>
                  </StyledLabel>
                  <StyledTextarea
                    id="message"
                    name="message"
                    placeholder="Tell me about your project or how I can help..."
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={
                      formik.touched.message && formik.errors.message
                        ? "error"
                        : ""
                    }
                  />
                  {formik.touched.message && formik.errors.message && (
                    <StyledErrorMessage>
                      {formik.errors.message}
                    </StyledErrorMessage>
                  )}
                </StyledFormGroup>

                <StyledSubmitButton
                  type="submit"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? "Sending..." : "Send Message"}
                </StyledSubmitButton>

                <StyledRecaptchaNotice>
                  This site is protected by reCAPTCHA and the Google{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </a>{" "}
                  apply.
                </StyledRecaptchaNotice>
              </StyledContactForm>
            )}
          </StyledSection>
        )}

        {/* Service Details Modal */}
        {selectedService && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
            onClick={() => setSelectedService(null)}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '80vh',
                overflow: 'auto',
                padding: '2rem',
                position: 'relative',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedService(null)}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.5rem',
                  lineHeight: '1'
                }}
                aria-label="Close modal"
              >
                ×
              </button>

              <h2 style={{ marginTop: 0, color: '#2c3e50', marginBottom: '1rem' }}>
                {selectedService.title}
              </h2>

              {/* Detailed Description */}
              {selectedService.modalContent?.detailedDescription && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <p style={{ color: '#555', lineHeight: '1.7', fontSize: '1.05rem' }}>
                    {selectedService.modalContent.detailedDescription}
                  </p>
                </div>
              )}

              {/* How It Works / Process */}
              {selectedService.modalContent?.process && selectedService.modalContent.process.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.1rem', color: '#34495e', marginBottom: '0.75rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0.5rem' }}>
                    How It Works
                  </h3>
                  <ol style={{ color: '#555', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                    {selectedService.modalContent.process.map((step: string, index: number) => (
                      <li key={index} style={{ marginBottom: '0.5rem' }}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Benefits */}
              {selectedService.modalContent?.benefits && selectedService.modalContent.benefits.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.1rem', color: '#34495e', marginBottom: '0.75rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0.5rem' }}>
                    Benefits
                  </h3>
                  <ul style={{ color: '#555', lineHeight: '1.8', paddingLeft: '1.5rem', listStyle: 'none' }}>
                    {selectedService.modalContent.benefits.map((benefit: string, index: number) => (
                      <li key={index} style={{ marginBottom: '0.5rem', paddingLeft: '1.5rem', position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 0, color: '#27ae60', fontWeight: 'bold' }}>✓</span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Ideal For */}
              {selectedService.modalContent?.idealFor && selectedService.modalContent.idealFor.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.1rem', color: '#34495e', marginBottom: '0.75rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0.5rem' }}>
                    Perfect For
                  </h3>
                  <ul style={{ color: '#555', lineHeight: '1.8', paddingLeft: '0', listStyle: 'none' }}>
                    {selectedService.modalContent.idealFor.map((item: string, index: number) => (
                      <li key={index} style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                        <span style={{ color: '#608cdf', fontWeight: 'bold', fontSize: '1.2rem', flexShrink: 0, lineHeight: '1.5' }}>→</span>
                        <span style={{ flex: 1 }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Duration */}
              {selectedService.modalContent?.duration && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  borderLeft: '4px solid #2196f3'
                }}>
                  <strong style={{ color: '#1976d2' }}>Typical Duration: </strong>
                  <span style={{ color: '#555' }}>
                    {selectedService.modalContent.duration}
                  </span>
                </div>
              )}

              {/* Pricing */}
              {selectedService.pricing && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  marginBottom: '1.5rem'
                }}>
                  <strong style={{ color: '#2c3e50' }}>Pricing: </strong>
                  <span style={{ color: '#27ae60', fontSize: '1.1rem', fontWeight: '600' }}>
                    {selectedService.pricing}
                  </span>
                </div>
              )}

              {/* Testimonial */}
              {selectedService.modalContent?.testimonial && (
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: '#fff9e6',
                  borderRadius: '4px',
                  marginBottom: '1.5rem',
                  borderLeft: '4px solid #ffc107',
                  fontStyle: 'italic'
                }}>
                  <p style={{ color: '#555', lineHeight: '1.6', marginBottom: '0.5rem', fontSize: '1.05rem' }}>
                    "{selectedService.modalContent.testimonial.quote}"
                  </p>
                  <p style={{ color: '#777', fontSize: '0.9rem', marginTop: '0.5rem', textAlign: 'right' }}>
                    — {selectedService.modalContent.testimonial.author}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </StyledBioPageContainer>
    </HelmetProvider>
  );
}
